/**
 * Admin — choix du modèle du panneau simulation travaux (V1 / V2).
 *
 * La sélection est appliquée immédiatement : la page résultat DPE lit
 * GET /scenarios-panel-variant (cache 5 min côté client). Chaque
 * changement est audité côté serveur. Pattern identique à
 * /admin/home-variant.
 */
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import {
  getAdminScenariosPanelVariant,
  setAdminScenariosPanelVariant,
} from "@/api/admin";

export default function AdminScenariosPanelVariantPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "scenarios-panel-variant"],
    queryFn: getAdminScenariosPanelVariant,
  });

  const mut = useMutation({
    mutationFn: (id: string) => setAdminScenariosPanelVariant(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin", "scenarios-panel-variant"] }),
  });

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-6 py-12">
        <Link to="/admin" className="text-sm text-blue-900 underline">
          ← Admin
        </Link>
        <h1 className="mt-3 text-2xl font-bold">Simulation travaux</h1>
        <p className="mt-1 text-sm text-gray-600">
          Choisissez le modèle du panneau « Simuler des travaux » servi aux
          utilisateurs. Le changement est immédiat, sans redéploiement.
        </p>

        {isLoading && <p className="mt-6 text-sm text-gray-500">Chargement…</p>}
        {error && (
          <p className="mt-6 text-sm text-red-700">Impossible de charger les modèles.</p>
        )}

        {data && (
          <div className="mt-6 space-y-3">
            {data.variants.map((v) => {
              const active = v.id === data.current;
              return (
                <button
                  key={v.id}
                  type="button"
                  disabled={mut.isPending || active}
                  onClick={() => mut.mutate(v.id)}
                  aria-pressed={active}
                  className={`flex w-full items-start gap-3 rounded-lg border-2 bg-white p-4 text-left transition ${
                    active
                      ? "border-amber-500"
                      : "border-gray-200 hover:border-blue-700 hover:shadow"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 h-5 w-5 flex-none rounded-full border-2 ${
                      active ? "border-amber-500 bg-amber-500" : "border-gray-300"
                    }`}
                  />
                  <span>
                    <span className="flex items-center gap-2 font-medium text-gray-800">
                      {v.label}
                      {active && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                          Actif
                        </span>
                      )}
                    </span>
                    <span className="mt-1 block text-sm text-gray-500">
                      {v.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <p className="mt-6 text-xs text-gray-400">
          Astuce : ouvrez la page résultat d'un logement dans un autre onglet
          pour comparer les deux modèles avant de publier. Le retour à V1 est
          instantané et sans risque (modèle de données identique).
        </p>
      </div>
    </Layout>
  );
}
